<template>
  <div>
    <TimelineDesktop
      v-if="!isMobile"
      :bookings="bookings"
      :counts="counts"
      :filter="filter"
      :tab-status="tab_status"
      :visible-tabs="visibleTabs"
      :today-date="todayDate"
      :today-time="todayTime"
      :selected-booking="selectedBooking"
      :actual-in-out="actualInOut"
      @change-status="changeStatus"
      @switch-tab="switchTabStatus"
      @select-booking="selectBookingHandler"
      @refresh="refreshHandler"
      :checkoutCodes="checkoutCodes"
      :checkinCodes="checkinCodes"
    />

    <TimelineMobile
      v-else
      :bookings="bookings"
      :counts="counts"
      :filter="filter"
      :tab-status="tab_status"
      :visible-tabs="visibleTabs"
      :today-date="todayDate"
      :today-time="todayTime"
      :selected-booking="selectedBooking"
      :actual-in-out="actualInOut"
      @change-status="changeStatus"
      @switch-tab="switchTabStatus"
      @refresh="refreshHandler"
      :checkinCodes="checkinCodes"
    />
  </div>
</template>

<script>
import moment from "moment";
import TimelineDesktop from "./components/TimelineDesktop.vue";
import TimelineMobile from "./components/TimelineMobile.vue";

// Minimum gap between notification sounds. Back-to-back check-ins (or a PMS
// sync burst) still refresh the list every time, but only the first one in the
// window is audible, so the front desk does not get a rapid string of beeps.
const SOUND_COOLDOWN_MS = 10000;

export default {
  components: {
    TimelineDesktop,
    TimelineMobile,
  },

  props: {
    timeline: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isMobile: false,
      slug_name: this.$route.params.slug_name,
      todayDate: moment().format("YYYY / MM / DD"),
      todayTime: moment().format("HH:mm"),
      modal: null,
      isLoaded: false,
      isDataLoaded: false,
      bookings: [],
      selectedBooking: null,
      clicked_book: null,
      counts: {
        all: 0,
        pci: 0,
        ci: 0,
        due_ci: 0,
        due_co: 0,
        co: 0,
      },
      filter: {
        status: "all",
      },
      tab_status: "checkin",
      tabs: {
        checkin: [
          {
            value: "all",
            label: "account_page_timeline.all",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
          {
            value: "pci",
            label: "account_page_timeline.pci",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
          {
            value: "ci",
            label: "account_page_timeline.ci",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
          {
            value: "due_ci",
            label: "account_page_timeline.due_ci",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
        ],

        checkout: [
          {
            value: "co",
            label: "account_page_timeline.co",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
          {
            value: "due_co",
            label: "account_page_timeline.due_co",
            activeClass: "btn-primary text-white",
            inActiveClass: "btn-light border",
            badgeActive: "bg-dark-blue",
            badgeInActive: "bg-secondary",
          },
        ],
      },
      checkoutCodes: ["102", "103", "109", "110", "111", "116"],
      checkinCodes: ["201", "208"],
      // Server events
      eventSource: null,
      audio: null, // Variable to hold the audio object,
      audioUnlocked: false,

      // NEW state. Delete these three when reverting every method below;
      // sseRetryTimer + isDestroyed belong to the SSE reconnect fix,
      // lastSoundAt to the sound cooldown.
      sseRetryTimer: null,
      isDestroyed: false,
      lastSoundAt: 0,

      // OLD state, used only by the commented-out handleTimelineUpdate:
      // isTimelineCooldown: false,
      // cooldownTimer: null,
    };
  },

  computed: {
    visibleTabs() {
      return this.tabs[this.tab_status] || [];
    },
  },

  async mounted() {
    this.checkScreen();
    this.initAudio();

    window.addEventListener("resize", this.checkScreen);
    await this.connectSSE();
    await this.fetchData();
  },

  beforeDestroy() {
    // NEW line (SSE reconnect fix) — delete when reverting connectSSE.
    this.isDestroyed = true;
    this.disconnectSSE();
    window.removeEventListener("resize", this.checkScreen);
    window.removeEventListener("touchstart", this.unlockAudio);
    window.removeEventListener("click", this.unlockAudio);
  },

  methods: {
    checkScreen() {
      this.isMobile = window.innerWidth < 768;
    },

    async fetchData() {
      this.isLoaded = false;

      try {
        const response = await this.$lionheart.get(
          `/v2/guest-timeline/${this.slug_name}`,
          {
            params: {
              status: this.filter.status,
            },
          }
        );

        this.bookings = response.data.data.bookings;
        this.counts = response.data.data.counts;

        this.isDataLoaded = true;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoaded = true;
      }
    },

    selectBookingHandler(booking) {
      this.clicked_book = booking;
      this.getBookingDetail();
    },

    async getBookingDetail() {
      const booking = this.clicked_book;
      try {
        const response = await this.$lionheart.get(
          `/v2/guest-timeline/${this.slug_name}/booking`,
          {
            params: {
              reservation_no: booking?.reservation_no ?? "",
              pass: booking?.pass ?? "",
            },
          }
        );
        this.selectedBooking = response.data.data.booking_details;
      } catch (error) {
        console.error(error);
      }
    },

    changeStatus(status) {
      this.filter.status = status;

      if (["all", "pci", "ci", "due_ci"].includes(status)) {
        this.tab_status = "checkin";
      }

      if (["co", "due_co"].includes(status)) {
        this.tab_status = "checkout";
      }

      this.fetchData();
    },

    async switchTabStatus() {
      this.tab_status = this.tab_status === "checkin" ? "checkout" : "checkin";

      this.filter.status = this.tab_status === "checkin" ? "all" : "co";

      await this.$nextTick();

      this.fetchData();
    },

    actualInOut(booking) {
      let timestamp = null;
      const statusCode = booking.checkin_status_code;

      // Determine which timestamp to use based on the status code
      if (statusCode == 101) {
        timestamp = booking.checked_in_at;
      } else if (statusCode == 112) {
        timestamp = booking.pre_checkin_at;
      } else if (this.checkoutCodes.includes(statusCode)) {
        timestamp = booking.checked_out_at;
      }

      // Return the formatted time or the fallback dash
      return timestamp ? moment(timestamp).format("HH:mm") : "--";
    },

    async refreshHandler() {
      this.isLoaded = false;

      try {
        // Refresh the booking list
        await this.fetchData();
        this.todayDate = moment().format("YYYY / MM / DD");
        this.todayTime = moment().format("HH:mm");

        // Refresh the selected booking if one is open
        if (this.clicked_book) {
          await this.getBookingDetail();
        }
      } finally {
        this.isLoaded = true;
      }
    },

    // ---------------------------------------------------------------------
    // OLD connectSSE — revert by uncommenting this and deleting the active
    // connectSSE below. This version closed and recreated the connection on
    // every error; because a fresh EventSource sends no Last-Event-ID, any
    // check-in landing in the gap was dropped (no refresh, no sound).
    // ---------------------------------------------------------------------
    // connectSSE() {
    //   const url = `${this.$lionheart.defaults.baseURL}/v2/guest-timeline/${this.slug_name}/events`;
    //
    //   this.eventSource = new EventSource(url);
    //
    //   this.eventSource.onopen = () => {
    //     console.log("SSE connected");
    //   };
    //
    //   this.eventSource.addEventListener("timeline-update", (e) => {
    //     try {
    //       const payload = JSON.parse(e.data);
    //       console.log(payload);
    //       this.handleTimelineUpdate(payload);
    //     } catch (err) {
    //       console.error("Invalid SSE payload:", err);
    //     }
    //   });
    //
    //   this.eventSource.onerror = (error) => {
    //     console.error("SSE error:", error);
    //     this.disconnectSSE();
    //     console.log("Reconnecting SSE...");
    //     this.connectSSE();
    //   };
    // },

    connectSSE() {
      if (this.isDestroyed) return;

      const url = `${this.$lionheart.defaults.baseURL}/v2/guest-timeline/${this.slug_name}/events`;

      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        console.log("SSE connected");
      };

      this.eventSource.addEventListener("timeline-update", (e) => {
        try {
          const payload = JSON.parse(e.data);
          console.log(payload);
          this.handleTimelineUpdate(payload);
        } catch (err) {
          console.error("Invalid SSE payload:", err);
        }
      });

      this.eventSource.onerror = (error) => {
        // The server ends the stream on purpose after delivering an event and
        // after its idle cap, so onerror fires on every normal cycle with
        // readyState === CONNECTING. Let EventSource reconnect itself there: it
        // resends Last-Event-ID and the stream resumes exactly where it stopped.
        // Closing and recreating the connection here would send no
        // Last-Event-ID, and any check-in landing in the gap would be lost.
        if (
          this.eventSource &&
          this.eventSource.readyState !== EventSource.CLOSED
        ) {
          return;
        }

        // Fatal error (non-200 response, wrong content type): the browser will
        // not retry on its own, so reconnect with a fixed delay.
        console.error("SSE closed, reconnecting:", error);
        this.disconnectSSE();

        if (this.isDestroyed) return;
        this.sseRetryTimer = setTimeout(this.connectSSE, 5000);
      };
    },

    // ---------------------------------------------------------------------
    // OLD disconnectSSE — revert by uncommenting this and deleting the active
    // disconnectSSE below. No retry-timer clear and no onerror teardown, so a
    // pending reconnect could fire after the component was destroyed.
    // ---------------------------------------------------------------------
    // disconnectSSE() {
    //   if (this.eventSource) {
    //     this.eventSource.close();
    //     this.eventSource = null;
    //   }
    // },

    disconnectSSE() {
      if (this.sseRetryTimer) {
        clearTimeout(this.sseRetryTimer);
        this.sseRetryTimer = null;
      }

      if (this.eventSource) {
        this.eventSource.onerror = null;
        this.eventSource.close();
        this.eventSource = null;
      }
    },

    // ---------------------------------------------------------------------
    // OLD handleTimelineUpdate — revert by uncommenting this and deleting the
    // active handleTimelineUpdate below. Note it also needs isTimelineCooldown
    // and cooldownTimer restored in data().
    // ---------------------------------------------------------------------
    // async handleTimelineUpdate(payload) {
    //   const allowedStatuses = ["pci", "ci", "co"];
    //
    //   // Only process pci and ci
    //   if (!allowedStatuses.includes(payload.status)) return;
    //
    //   // Ignore if still in cooldown
    //   // if (this.isTimelineCooldown) return;
    //
    //   // Start cooldown
    //   this.isTimelineCooldown = true;
    //
    //   await this.refreshHandler();
    //   await this.playAudio();
    //   // Release cooldown after 5 seconds
    //   // this.cooldownTimer = setTimeout(() => {
    //   //   this.isTimelineCooldown = false;
    //   // }, 5000);
    // },

    async handleTimelineUpdate(payload) {
      const allowedStatuses = ["pci", "ci", "co"];

      // Only process pci, ci and co
      if (!allowedStatuses.includes(payload.status)) return;

      // Play first so the sound is not delayed by the refresh round trip.
      // The 10s cooldown lives in playAudio(), so the refresh below still runs
      // on every event even when the sound is suppressed.
      this.playAudio();
      await this.refreshHandler();
    },

    // ---------------------------------------------------------------------
    // OLD initAudio — revert by uncommenting this and deleting the active
    // initAudio below. Must be reverted together with unlockAudio: `once: true`
    // meant a failed first gesture burned the only chance to unlock audio.
    // ---------------------------------------------------------------------
    // initAudio() {
    //   this.audio = new Audio("/sounds/notification.mp3");
    //   this.audio.preload = "auto";
    //
    //   window.addEventListener("touchstart", this.unlockAudio, { once: true });
    //   window.addEventListener("click", this.unlockAudio, { once: true });
    // },

    initAudio() {
      this.audio = new Audio("/sounds/notification.mp3");
      this.audio.preload = "auto";

      // Not `once: true`: if the first gesture fails to unlock (autoplay policy
      // rejects, audio not loaded yet) the listener must stay so a later tap
      // can retry. unlockAudio() removes them itself once it succeeds.
      window.addEventListener("touchstart", this.unlockAudio);
      window.addEventListener("click", this.unlockAudio);
    },

    // ---------------------------------------------------------------------
    // OLD unlockAudio — revert by uncommenting this and deleting the active
    // unlockAudio below. Revert together with initAudio.
    // ---------------------------------------------------------------------
    // async unlockAudio() {
    //   if (this.audioUnlocked) return;
    //
    //   try {
    //     // Unlock only, no notification sound
    //     this.audio.muted = true;
    //     await this.audio.play();
    //     this.audio.pause();
    //     this.audio.currentTime = 0;
    //     this.audio.muted = false;
    //
    //     this.audioUnlocked = true;
    //   } catch (err) {
    //     console.error(err);
    //   }
    // },

    async unlockAudio() {
      if (this.audioUnlocked) return;

      try {
        // Unlock only, no notification sound
        this.audio.muted = true;
        await this.audio.play();
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.muted = false;

        this.audioUnlocked = true;
        window.removeEventListener("touchstart", this.unlockAudio);
        window.removeEventListener("click", this.unlockAudio);
      } catch (err) {
        // Leave it unmuted so a later retry is not silently muted.
        this.audio.muted = false;
        console.error(err);
      }
    },

    // ---------------------------------------------------------------------
    // OLD playAudio — revert by uncommenting this and deleting the active
    // playAudio below. No cooldown: every event played a sound. Reverting this
    // one alone is safe, it is independent of the SSE reconnect fix.
    // ---------------------------------------------------------------------
    // async playAudio() {
    //   if (!this.audioUnlocked) return;
    //
    //   try {
    //     this.audio.pause();
    //     this.audio.currentTime = 0;
    //     await this.audio.play();
    //   } catch (err) {
    //     console.error(err);
    //   }
    // },

    async playAudio() {
      if (!this.audioUnlocked) return;

      // 10s cooldown. Suppresses the sound only: the caller still refreshes the
      // list, so a guest checking in during the cooldown shows up right away.
      // const now = Date.now();
      // if (now - this.lastSoundAt < SOUND_COOLDOWN_MS) return;

      // Stamped before play() so two events arriving in the same tick cannot
      // both get through.
      // this.lastSoundAt = now;

      try {
        this.audio.pause();
        this.audio.currentTime = 0;
        await this.audio.play();
      } catch (err) {
        console.error(err);
      }
    },
  },
};
</script>